import React from 'react'
import { cx } from 'class-variance-authority'
import { ChevronLeftIcon } from '@heroicons/react/20/solid'
import { navigate } from 'gatsby'
import { Offer } from './mapper'
import ButtonColor from '../../ui/buttonColor'
import { useApplyStore } from '../../../stores/ui/applyStore'
import Button from '../../ui/button'

interface FixedHeaderProps {
  data: Offer
  isCareerSite: boolean
  isVisible: boolean
}

export const FixedHeader = ({
  data,
  isCareerSite,
  isVisible,
}: FixedHeaderProps) => {
  const { title } = data
  const color = data.office.firm.colorCareerSite || undefined
  const openApplicationFormModal = useApplyStore(
    (state) => state.openApplicationFormModal,
  )

  const handleClick = () => {
    openApplicationFormModal({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      offerId: data.id!,
      colorCareerSite: color || undefined,
      isCareerSiteApply: Boolean(color),
    })
  }

  const tags = getTags(data)

  return (
    <section
      className={cx(
        'fixed inset-x-0 bottom-0 md:bottom-full md:top-0 bg-white min-h-[80px] z-10 border border-b-lamacompta-blue/30 motion-translate-x-in-[-1%] motion-translate-y-in-[124%] md:motion-translate-y-in-[-124%] transition-all duration-300 ease-in-out shadow-sm pb-14 md:pb-0',
        isVisible ? 'visible' : 'hidden motion-translate-y-in-[-1%]',
      )}
    >
      <div
        style={{
          backgroundColor: color && isCareerSite ? `${color}20` : undefined,
        }}
      >
        <div className="text-night flex items-center justify-between xl:w-[1115px] lg:w-[950px] lg:mx-auto 2xl:w-[1300px] mx-auto py-3 px-3 md:px-0">
          <div className="flex items-center max-w-[70%] md:space-x-4 lg:space-x-6">
            <div className="items-center hidden md:flex">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center space-x-1 focus:outline-none"
              >
                <ChevronLeftIcon className="w-6 h-6 text-night" />
                <span>Retour</span>
              </button>
            </div>
            <div className="space-y-1">
              <p className="text-lg font-semibold">{title}</p>
              <ul className="hidden space-x-2 md:flex">
                {tags.map(({ label, bgColor, textColor }) => (
                  <Tag
                    key={label}
                    label={label}
                    bgColor={bgColor}
                    textColor={textColor}
                  />
                ))}
              </ul>
              <ul className="flex space-x-2 md:hidden">
                {tags.slice(0, 3).map(({ label, bgColor, textColor }) => (
                  <Tag
                    key={label}
                    label={label}
                    bgColor={bgColor}
                    textColor={textColor}
                  />
                ))}
              </ul>
            </div>
          </div>

          {!data.positionFilled && <div className="z-10">
            {isCareerSite && color ? (
              <ButtonColor
                color={color}
                label="Postuler"
                invert
                size="medium"
                onClick={handleClick}
              />
            ) : (
              <Button
                label="Postuler"
                color="primary"
                invert
                size="medium"
                onClick={handleClick}
              />
            )}
          </div>}
        </div>
      </div>
    </section>
  )
}

const Tag = ({
  label,
  bgColor,
  textColor,
}: {
  label: string
  bgColor: string
  textColor: string
}) => (
  <span
    className={`px-2 py-1 capitalize rounded-xl transition-colors duration-800 ease-in-out text-[12px] truncate ${bgColor} ${textColor}`}
  >
    {label}
  </span>
)

function getTags(data: FixedHeaderProps['data']): {
  label: string
  bgColor: string
  textColor: string
}[] {
  const tags: {
    label: string
    bgColor: string
    textColor: string
  }[] = []
  const { office, contractType, hoursPerWeek, payRange } = data

  if (payRange) {
    tags.push({
      label: `${payRange}`,
      bgColor: 'bg-lama-yellow/40 hover:bg-lama-yellow/60',
      textColor: 'text-night',
    })
  }
  if (contractType) {
    tags.push({
      label: contractType,
      bgColor: 'bg-yellow-100 hover:bg-yellow-200',
      textColor: 'text-night',
    })
  }

  if (hoursPerWeek) {
    tags.push({
      label: `${hoursPerWeek}`,
      bgColor: 'bg-green-100 hover:bg-green-200',
      textColor: 'text-night',
    })
  }

  if (office && office.city) {
    tags.push({
      label: office.city,
      bgColor: 'bg-light-blue/50 hover:bg-light-blue/70',
      textColor: 'text-night',
    })
  }
  return tags
}
